import React, { useState } from 'react';
import { QuickInfoStep } from '@/components/questionnaire/QuickInfoStep';
import { GuidedQuestionnaire } from '@/components/questionnaire/GuidedQuestionnaire';
import { QuestionnaireHeader } from '@/components/questionnaire/QuestionnaireHeader';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { CarrierMatchingEngine, type UserProfile } from '@/components/CarrierMatchingEngine';
import { trackFunnelStep } from '@/utils/analytics';
import { AIAssistantWidget } from '@/components/AIAssistantWidget';
import { ProgressiveSignup } from '@/components/signup/ProgressiveSignup';
import { ProfileDashboard } from '@/components/dashboard/ProfileDashboard';
import { QuestionaireSummary } from '@/components/questionnaire/QuestionaireSummary';
import { useAuth } from '@/components/AuthProvider';

interface InitialFormData {
  name: string;
  age: string;
  state: string;
  coverageAmount: string;
}

interface QuestionnaireData extends InitialFormData {
  smokingStatus?: string;
  policyType?: string;
  healthStatus?: string;
  majorHealthConditions?: string;
  medications?: string;
  familyHistory?: string;
  riskyHobbies?: string;
  highRiskOccupation?: string;
  alcoholConsumption?: string;
  exercise?: string;
  priority?: string;
  noMedicalExam?: string;
  approvalPreference?: string;
}

interface QuestionnaireFlowProps {
  isVisible: boolean;
  onClose: () => void;
}

export const QuestionnaireFlow: React.FC<QuestionnaireFlowProps> = ({ isVisible, onClose }) => {
  const { user, loading } = useAuth();
  const [currentStep, setCurrentStep] = useState<'quickInfo' | 'questionnaire' | 'summary' | 'emailOnly' | 'verifyEmail' | 'profileInfo' | 'createPassword' | 'dashboard'>('quickInfo');
  const [formData, setFormData] = useState<QuestionnaireData>({
    name: '',
    age: '',
    state: '',
    coverageAmount: ''
  });
  const [recommendation, setRecommendation] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);

  // If user is authenticated and onboarding is complete, show dashboard
  React.useEffect(() => {
    if (!loading && user) {
      supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.onboarding_completed) {
            setCurrentStep('dashboard');
          }
        });
    }
  }, [user, loading]);

  if (!isVisible) return null;

  const handleQuickInfoSubmit = async (data: InitialFormData) => {
    setFormData(prev => ({ ...prev, ...data }));
    setCurrentStep('questionnaire');
    trackFunnelStep('entered_chat'); // Using existing analytics event
  };

  const handleSeeQuote = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      console.log('Building user profile from form data:', formData);
      
      // Build user profile
      const userProfile: UserProfile = {
        name: formData.name,
        age: parseInt(formData.age),
        state: formData.state,
        coverageAmount: formData.coverageAmount,
        smokingStatus: formData.smokingStatus || 'no',
        policyType: formData.policyType,
        healthStatus: formData.healthStatus,
        healthConditions: formData.majorHealthConditions,
        priority: formData.priority,
        noMedicalExam: formData.noMedicalExam,
        approvalPreference: formData.approvalPreference
      };

      // Get carrier recommendation
      const matchingEngine = new CarrierMatchingEngine();
      const bestMatch = matchingEngine.getBestRecommendation(userProfile);
      
      if (bestMatch) {
        const rec = {
          recommendedCompany: bestMatch.carrier.name,
          referralLink: bestMatch.carrier.referralLink,
          description: bestMatch.carrier.description,
          score: bestMatch.score,
          reasons: bestMatch.reasons,
          carrierId: bestMatch.carrier.id
        };
        
        setRecommendation(rec);
        console.log('Generated recommendation:', rec);
      }

      // Fire-and-forget analytics
      try {
        trackFunnelStep('quote_delivered');
      } catch (err) {
        console.warn('Analytics error:', err);
      }
      
      // Save to Supabase if user is authenticated (non-blocking)
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user) {
          supabase
            .from('profiles')
            .upsert({
              user_id: user.id,
              name: formData.name,
              age: parseInt(formData.age),
              state: formData.state,
              coverage_amount: formData.coverageAmount,
              smoking_status: formData.smokingStatus,
              policy_type: formData.policyType,
              health_status: formData.healthStatus,
              health_conditions: formData.majorHealthConditions,
              priority: formData.priority,
              no_medical_exam: formData.noMedicalExam,
              approval_preference: formData.approvalPreference
            })
            .then((result) => {
              if (result.error) {
                console.warn('Profile save error:', result.error);
              } else {
                console.log('Profile saved successfully');
              }
            });
        }
      }).catch((err: any) => console.warn('Auth check error:', err));
      
      // Transition to summary immediately
      setCurrentStep('summary');
      
    } catch (error) {
      console.error('Error generating quote:', error);
      // Keep user on page and show error if needed
      // Could add a toast here
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignupComplete = (user: any) => {
    setCurrentStep('dashboard');
  };

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen">
        <QuestionnaireHeader 
          currentStep={currentStep}
          onClose={onClose}
        />
        
        <div className="container mx-auto px-4 py-8">
          {currentStep === 'quickInfo' && (
            <QuickInfoStep 
              onSubmit={handleQuickInfoSubmit}
            />
          )}
          
          {currentStep === 'questionnaire' && (
            <GuidedQuestionnaire 
              initialData={formData}
              onQuestionnaireUpdate={(data) => setFormData(prev => ({ ...prev, ...data }))}
              onSeeQuote={handleSeeQuote}
              submitting={submitting}
            />
          )}
          
          {currentStep === 'summary' && recommendation && (
            <QuestionaireSummary 
              data={formData}
              recommendation={recommendation}
              onGetQuote={() => {
                // The affiliate click is handled in the component
                trackFunnelStep('referral_link_clicked');
              }}
              onStartOver={() => {
                setCurrentStep('quickInfo');
                setFormData({
                  name: '',
                  age: '',
                  state: '',
                  coverageAmount: ''
                });
                setRecommendation(null);
              }}
            />
          )}

          {(currentStep === 'emailOnly' || currentStep === 'verifyEmail' || currentStep === 'profileInfo' || currentStep === 'createPassword') && (
            <div className="flex justify-center">
              <ProgressiveSignup 
                initialData={{
                  name: formData.name,
                  age: formData.age,
                  state: formData.state,
                  coverageAmount: formData.coverageAmount,
                  smokingStatus: formData.smokingStatus || 'no'
                }}
                onSignupComplete={handleSignupComplete}
              />
            </div>
          )}

          {currentStep === 'dashboard' && user && (
            <ProfileDashboard />
          )}
        </div>
        
        {/* AI Assistant Widget */}
        <AIAssistantWidget 
          pageContext="quote" 
          userInfo={currentStep === 'questionnaire' ? formData : undefined} 
        />
      </div>
    </div>
  );
};